import type { CSSProperties } from "react";
import type { TargetRequest } from "../api/types";
import type { Theme } from "../theme/pixel";
import { ghostBtn, primaryBtn } from "../theme/styles";

interface Props {
  request: TargetRequest;
  pickCount: number;
  onConfirm: () => void;
  onClear: () => void;
  theme: Theme;
  animating: boolean;
}

export function TargetOverlay({
  request,
  pickCount,
  onConfirm,
  onClear,
  theme,
  animating,
}: Props) {
  const min = request.min;
  const max = request.max;
  const isRange = min !== max;
  const requirement = isRange ? `${min}-${max}` : `${max}`;
  const ready = pickCount >= min && pickCount <= max;
  // Peek mode: min == max == 0 means the script wanted a view-only
  // prompt (peek() or a 0/0 choose_targets used as an
  // acknowledge-and-continue gate). Render a single DONE button —
  // there's nothing to clear because there's nothing to pick.
  const isPeekOnly = min === 0 && max === 0;
  const bar: CSSProperties = {
    position: "absolute",
    top: 6,
    left: "50%",
    transform: "translateX(-50%)",
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "6px 12px",
    background: theme.panel,
    border: `2px solid ${theme.accent}`,
    fontFamily: theme.fontDisplay,
    fontSize: 11,
    letterSpacing: theme.letterSpacing,
    color: theme.ink,
    zIndex: 30,
  };
  if (isPeekOnly) {
    return (
      <div style={bar}>
        <span style={{ color: theme.accent }}>
          {theme.ornament} {request.prompt ?? "Look"} {theme.ornament}
        </span>
        <button
          onClick={onConfirm}
          disabled={animating}
          {...primaryBtn(theme, animating)}
        >
          DONE
        </button>
      </div>
    );
  }
  return (
    <div style={bar}>
      <span style={{ color: theme.accent }}>
        {theme.ornament} {request.prompt ?? "Choose targets"} {theme.ornament}
      </span>
      <span style={{ color: theme.inkDim }}>
        {pickCount} / {requirement}
      </span>
      <button
        onClick={onClear}
        disabled={pickCount === 0 || animating}
        {...ghostBtn(theme, pickCount === 0 || animating)}
      >
        CLEAR
      </button>
      <button
        onClick={onConfirm}
        disabled={!ready || animating}
        {...primaryBtn(theme, !ready || animating)}
      >
        CONFIRM
      </button>
    </div>
  );
}
